var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { AbortableSubmitEventListener, ChangeEventListener, EditEventListener, SubmitEventListener, ValidateEvent, ValidateEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { createAbort } from '@philips-internal/oswald-ui-utils';
class FormAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onEdit = new EditEventListener();
        /** @event */
        this.onBeforeSubmit = new AbortableSubmitEventListener();
        /** @event */
        this.onSubmit = new SubmitEventListener();
        /** @event */
        this.onBeforeCancel = new AbortableSubmitEventListener();
        /** @event */
        this.onCancel = new SubmitEventListener();
        /** @event */
        this.onChange = new ChangeEventListener();
        /** @event */
        this.onValidate = new ValidateEventListener();
    }
    /**
     * Returns the form title.
     */
    getTitle() {
        return this.state.title;
    }
    /**
     * Updates the form title.
     *
     * @param title - The new title.
     */
    setTitle(title) {
        this.setState({ title });
    }
    /**
     * Returns the form data.
     */
    getData() {
        return this.state.data;
    }
    /**
     * Updates the form data.
     *
     * @param data - The new data.
     */
    setData(data) {
        this.setState({ data });
    }
    /**
     * Returns the pretty record.
     */
    getPrettyRecord() {
        return this.state.prettyRecord;
    }
    /**
     * Returns the form record.
     */
    getRecord() {
        const { record, recordChanges } = this.state;
        return { ...record, ...recordChanges };
    }
    /**
     * Updates the form record.
     *
     * @param record - The new record.
     */
    setRecord(record) {
        this.setState({ record, recordChanges: {} });
    }
    /**
     * Returns if the form is disabled.
     */
    isDisabled() {
        return this.state.disabled;
    }
    /**
     * Sets if the form should be disabled.
     *
     * @param disabled - If the form should be disabled.
     */
    setDisabled(disabled) {
        this.setState({ disabled });
    }
    /**
     * Returns the form density.
     */
    getDensity() {
        return this.state.density;
    }
    /**
     * Updates the form density.
     *
     * @param density - The new density.
     */
    setDensity(density) {
        this.setState({ density });
    }
    /**
     * Returns if the form is editing state.
     */
    isEditing() {
        return this.state.innerEditing;
    }
    /**
     * Updates the form editing state.
     * If the form is locked, will only take effect if force is true.
     *
     * @param innerEditing - The new editing state.
     * @param force - If the locked property should be ignored.
     */
    setEditing(innerEditing, force) {
        if (this.state.locked && !force) {
            return;
        }
        this.setState({ innerEditing });
    }
    /**
     * Returns the form's current editing mode.
     */
    getEditingMode() {
        return this.state.editingMode;
    }
    /**
     * Updates the current editing mode.
     *
     * @param editingMode - The new Form editing mode.
     */
    setEditingMode(editingMode) {
        this.setState({ editingMode });
    }
    /**
     * Triggers the form's submit events.
     */
    submit() {
        if (!this.validate())
            return;
        const abort = createAbort();
        const prettyRecord = this.getPrettyRecord();
        const record = this.getRecord();
        this.onBeforeSubmit.fire({ abort, prettyRecord, record });
        void abort.promise.then(aborted => {
            if (aborted)
                return;
            this.onSubmit.fire({ prettyRecord, record });
            this.setEditing(false);
        });
    }
    /**
     * Cancels the form's editing.
     */
    cancel() {
        const abort = createAbort();
        const record = this.getRecord();
        const prettyRecord = this.getPrettyRecord();
        this.onBeforeCancel.fire({ abort, prettyRecord, record });
        void abort.promise.then(aborted => {
            if (aborted)
                return;
            this.onCancel.fire({ prettyRecord, record });
            this.setRecord(this.state.data);
            this.setEditing(false);
        });
    }
    /**
     * Returns the form record.
     */
    getValue() {
        return this.getRecord();
    }
    /**
     * Updates the form record.
     *
     * @param data - The new record.
     */
    setValue(data) {
        this.setRecord(data);
    }
    /**
     * Returns if the form is locked.
     */
    isLocked() {
        return this.state.locked;
    }
    /**
     * Updates if the form should be locked.
     *
     * @param locked - If the form should be locked.
     */
    setLocked(locked) {
        this.setState({ locked });
    }
    /**
     * Runs the forms validation and returns if the form is valid.
     */
    validate() {
        const { validate } = this.state.__validation;
        const [valid, validations] = validate(true);
        const event = new ValidateEvent({ valid, validations });
        this.onValidate.fire(event);
        return valid;
    }
    /**
     * Returns if the form is valid.
     */
    isValid() {
        const { validate } = this.state.__validation;
        const [valid] = validate(false);
        return valid;
    }
    /**
     * Returns if the form is loading.
     */
    isLoading() {
        return this.state.loading;
    }
    /**
     * Updates the loading state of the form.
     *
     * @param loading - If the form is loading.
     */
    setLoading(loading) {
        this.setState({ loading });
    }
    /**
     * Returns the save expression.
     */
    getSaveExpression() {
        return this.state.saveExpression;
    }
    /**
     * Updates the save expression.
     *
     * @param saveExpression - The save expression.
     */
    setSaveExpression(saveExpression) {
        this.setState({ saveExpression });
    }
    /**
     * Returns the cancel expression.
     */
    getCancelExpression() {
        return this.state.cancelExpression;
    }
    /**
     * Updates the cancel expression.
     *
     * @param cancelExpression - The cancel expression.
     */
    setCancelExpression(cancelExpression) {
        this.setState({ cancelExpression });
    }
    /**
     * Resets the form validation.
     */
    reset() {
        this.state.__validation.reset();
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onEdit", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onBeforeSubmit", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onSubmit", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onBeforeCancel", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onCancel", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onChange", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], FormAPI.prototype, "onValidate", void 0);
export default FormAPI;
//# sourceMappingURL=form-api.js.map